/**
 * Bots Service
 * Service for managing lending bot instances
 */

import { api } from './api'

export interface BotConfig {
  strategy?: string
  min_daily_rate: number
  max_daily_rate?: number
  gap_mode?: 'raw' | 'rawbtc' | 'relative'
  gap_bottom: number
  gap_top: number
  spread_lend?: number
  min_loan_size?: number
  max_active_amount?: number
  max_to_lend?: number
  max_percent_to_lend?: number
  xday_threshold?: number
  xdays?: number
  auto_transfer?: boolean
  dry_run?: boolean
  update_interval?: number
}

export interface BotInstance {
  id: number
  api_key_id: number
  api_key_label?: string | null
  name: string
  currency: string
  status: 'active' | 'paused' | 'stopped' | 'error'
  config: BotConfig
  workflow_id?: string | null
  workflow_status?: string | null
  last_cycle_at?: number | null
  total_lent: number
  total_earned: number
  error_message?: string | null
  created_at: number
  updated_at: number
}

export interface CreateBotRequest {
  api_key_id: number
  name: string
  currency: string
  config: BotConfig
}

export interface UpdateBotRequest {
  name?: string
  config?: Partial<BotConfig>
  status?: 'active' | 'paused' | 'stopped' | 'error'
}

export interface ListBotsResponse {
  bots: BotInstance[]
}

export interface CreateBotResponse {
  bot: BotInstance
  message: string
}

export interface BotResponse {
  bot: BotInstance
}

export interface DeleteBotResponse {
  success: boolean
  message: string
}

export interface BotActionResponse {
  success: boolean
  message: string
}

// ============================================================================
// Bot Monitoring & Logging Types
// ============================================================================

export interface BotLog {
  id: number
  bot_id: number
  level: 'debug' | 'info' | 'warning' | 'error'
  category: 'api_call' | 'order' | 'loan' | 'error' | 'system'
  message: string
  details: any | null
  created_at: number
}

export interface ListBotLogsParams {
  level?: 'debug' | 'info' | 'warning' | 'error'
  category?: 'api_call' | 'order' | 'loan' | 'error' | 'system'
  limit?: number
  offset?: number
}

export interface ListBotLogsResponse {
  logs: BotLog[]
  pagination: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
}

export interface BotStatus {
  bot_id: number
  currency: string
  status: string
  wallet_balance: number
  active_offers: number
  active_loans: number
  total_lent: number
  total_earned: number
  last_error: {
    message: string
    timestamp: number
  } | null
  last_updated: number | null
}

export interface BotStatusSnapshot {
  id: number
  bot_id: number
  wallet_balance: number
  active_offers: number
  active_loans: number
  total_lent: number
  total_earned: number
  last_error: string | null
  created_at: number
}

export interface ListBotSnapshotsParams {
  hours?: number
  limit?: number
}

export interface ListBotSnapshotsResponse {
  bot_id: number
  snapshots: BotStatusSnapshot[]
  time_range: {
    hours: number
    from: number
    to: number
  }
}

/**
 * Bots Service
 */
export const botsService = {
  /**
   * List all bot instances for current user
   */
  async list(): Promise<BotInstance[]> {
    const response = await api.get<ListBotsResponse>('/bots')
    return response.bots
  },

  /**
   * Get specific bot instance by ID
   */
  async get(id: number): Promise<BotInstance> {
    const response = await api.get<BotResponse>(`/bots/${id}`)
    return response.bot
  },

  /**
   * Create new bot instance
   */
  async create(data: CreateBotRequest): Promise<CreateBotResponse> {
    return api.post<CreateBotResponse>('/bots', data)
  },

  /**
   * Update bot instance
   */
  async update(id: number, data: UpdateBotRequest): Promise<void> {
    await api.patch(`/bots/${id}`, data)
  },

  /**
   * Delete bot instance
   */
  async delete(id: number): Promise<void> {
    await api.delete<DeleteBotResponse>(`/bots/${id}`)
  },

  /**
   * Start bot instance
   */
  async start(id: number): Promise<BotActionResponse> {
    return api.post<BotActionResponse>(`/bots/${id}/start`)
  },

  /**
   * Stop bot instance
   */
  async stop(id: number): Promise<BotActionResponse> {
    return api.post<BotActionResponse>(`/bots/${id}/stop`)
  },

  /**
   * Manually execute a lending cycle (refreshes bot data)
   */
  async executeCycle(id: number): Promise<BotActionResponse> {
    return api.post<BotActionResponse>(`/bots/${id}/execute-cycle`)
  },

  /**
   * Refresh all active bots (execute cycle for all active bots)
   */
  async refreshAll(): Promise<{ success: boolean; refreshed: number; errors: number }> {
    const bots = await this.list()
    const activeBots = bots.filter((bot) => bot.status === 'active')

    let refreshed = 0
    let errors = 0

    // Execute cycle for all active bots in parallel
    const results = await Promise.allSettled(
      activeBots.map((bot) => this.executeCycle(bot.id))
    )

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        refreshed++
      } else {
        errors++
      }
    })

    return { success: errors === 0, refreshed, errors }
  },

  // ============================================================================
  // Bot Monitoring & Logging Methods
  // ============================================================================

  /**
   * Get bot activity logs
   */
  async getLogs(id: number, params?: ListBotLogsParams): Promise<ListBotLogsResponse> {
    const queryParams: Record<string, string> = {}

    if (params?.level) queryParams.level = params.level
    if (params?.category) queryParams.category = params.category
    if (params?.limit) queryParams.limit = params.limit.toString()
    if (params?.offset) queryParams.offset = params.offset.toString()

    return api.get<ListBotLogsResponse>(`/bots/${id}/logs`, queryParams)
  },

  /**
   * Get bot current status
   */
  async getStatus(id: number): Promise<BotStatus> {
    return api.get<BotStatus>(`/bots/${id}/status`)
  },

  /**
   * Get bot status snapshots (historical data)
   */
  async getSnapshots(id: number, params?: ListBotSnapshotsParams): Promise<ListBotSnapshotsResponse> {
    const queryParams: Record<string, string> = {}

    if (params?.hours) queryParams.hours = params.hours.toString()
    if (params?.limit) queryParams.limit = params.limit.toString()

    return api.get<ListBotSnapshotsResponse>(`/bots/${id}/snapshots`, queryParams)
  },
}
